import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, MailCheck, KeyRound } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email address.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6" data-testid="forgot-password-page">
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      <main id="main-content" tabIndex={-1} className="w-full max-w-md outline-none">
        <div className="flex justify-center mb-8">
          <img src="/lifelink-logo.png" alt="LIFE Link" className="h-14 object-contain" />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-success">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#3d7a22]/10 mb-4">
                <MailCheck className="w-7 h-7 text-[#3d7a22]" aria-hidden="true" />
              </span>
              <h1 className="font-['Manrope'] text-2xl font-bold text-[#1A1A1A] mb-2">Check your email</h1>
              <p className="text-[#5C5C5C] text-sm leading-relaxed mb-6">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link.
                It expires in 60 minutes. Don't forget to check your spam folder.
              </p>
              <Link to="/login" className="text-[#34657f] font-semibold underline text-sm" data-testid="back-to-login-link">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#34657f]/10 mb-4">
                <KeyRound className="w-6 h-6 text-[#34657f]" aria-hidden="true" />
              </span>
              <h1 className="font-['Manrope'] text-2xl font-bold text-[#1A1A1A] mb-1">Forgot your password?</h1>
              <p className="text-[#5C5C5C] text-sm mb-6">
                Enter your email and we'll send you a link to reset it.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email Address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@projectlife.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 px-4 border-slate-300 focus:border-[#34657f]"
                    data-testid="forgot-email-input"
                  />
                </div>
                {error && (
                  <p className="text-red-700 text-sm" role="alert" data-testid="forgot-password-error">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#34657f] hover:bg-[#2a5060] text-white font-semibold rounded-lg"
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? 'Sending…' : 'Send Reset Link'}
                </Button>
              </form>
              <Link
                to="/login"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#34657f] hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
